import { Alert, Form, Input, InputNumber, Modal, Typography, message } from 'antd'
import { useEffect, useState } from 'react'
import { platformAdjustWorkspaceCredit } from '../api'
import type { AdminCreditAdjustmentResult } from '../types'

function mintRequestId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `adj-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

/** Stage S4 — ADMIN_CREDIT_ADJUST flow. `amount` is a single signed
 * InputNumber (positive = grant, negative = deduct) — the required
 * `note` is validated both here (client-side, blocking submit) and again
 * by the backend service (belt and suspenders, see
 * `plan_service.admin_adjust_credit`'s docstring). */
export default function CreditAdjustmentModal({
  open,
  onClose,
  workspaceId,
  workspaceName,
  creditBalance,
  onSuccess,
}: {
  open: boolean
  onClose: () => void
  workspaceId: string
  workspaceName: string
  creditBalance: string
  onSuccess: (result: AdminCreditAdjustmentResult) => void
}) {
  const [amount, setAmount] = useState<number | null>(null)
  const [note, setNote] = useState('')
  const [requestId, setRequestId] = useState(mintRequestId())
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setAmount(null)
    setNote('')
    setError(null)
    setRequestId(mintRequestId())
  }, [open])

  const before = Number(creditBalance)
  const after = before + (amount ?? 0)
  const willBeNegative = after < 0
  const noteEmpty = !note.trim()

  const onConfirm = async () => {
    if (amount === null || amount === 0) {
      setError('请输入非零调整数额')
      return
    }
    if (noteEmpty) {
      setError('调整原因（note）为必填项')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const res = await platformAdjustWorkspaceCredit(workspaceId, {
        amount,
        note: note.trim(),
        adjustment_request_id: requestId,
      })
      message.success(`已调整「${workspaceName}」积分：${amount > 0 ? '+' : ''}${amount}`)
      onSuccess(res.data)
      onClose()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={`调整积分 — ${workspaceName}`}
      open={open}
      onCancel={onClose}
      onOk={onConfirm}
      okText="确认调整"
      cancelText="取消"
      confirmLoading={submitting}
    >
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 12 }} />}
      <Form layout="vertical">
        <Form.Item label="调整数额（正数为增加，负数为扣减）" required>
          <InputNumber
            style={{ width: '100%' }}
            value={amount}
            onChange={(v) => setAmount(v)}
            placeholder="例如 500 或 -200"
          />
        </Form.Item>
        <Form.Item label="调整原因（必填）" required validateStatus={noteEmpty ? undefined : 'success'}>
          <Input.TextArea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="请说明本次人工调整积分的原因，将写入操作日志"
            rows={3}
          />
        </Form.Item>
        <Typography.Text type="secondary">
          调整前 {before} {amount !== null ? `→ 调整后 ${after}` : ''}
        </Typography.Text>
        {willBeNegative && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 8 }}
            message="调整后余额将为负数"
            description="系统允许负余额，但请确认这是预期行为。"
          />
        )}
        {/* Task spec §66 — clear (not alarmist) immutability disclosure
         * before every write; the record this creates can never be
         * deleted/edited afterward, only reversed (task spec §30/§65). */}
        <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          本操作将写入永久调整记录，历史记录不可删除；如操作有误，可在积分明细中对该记录发起「冲正」。
        </Typography.Text>
      </Form>
    </Modal>
  )
}
